import { showDialog } from '@adornis/dialog/dialog-functions.js';
import { EntityPickerInput } from '@adornis/entity-picker/client/entity-picker-input.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { DropdownSelectionInput } from '@adornis/formfield/fields/dropdown-selection-input.js';
import { TextInput } from '@adornis/formfield/fields/text-input.js';
import { useForm } from '@adornis/forms/use-form.js';
import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { Country } from '@adornis/internationalization/country.js';
import { LanguagePicker } from '@adornis/internationalization/language-iso639-2.js';
import { html, nothing } from 'lit';
import { createFieldEngineerWithUser } from '../../api/create-field-engineer-with-user.js';
import { getAllowedPartnerCompanies } from '../../api/scoped-queries.js';
import { ServiceNowEngineerPicker } from '../components/servicenow-pickers.js';
import { SmartTeamsCompany, SmartTeamsFieldEngineer, SmartTeamsLocation } from '../../db/index.js';

const CreateFieldEngineerForm = stateful(
  (props: {
    companies: SmartTeamsCompany[];
    onCreated: (id: string) => void;
    onCancel: () => void;
  }) => {
    const [password, setPassword] = useState('');
    const [isCreating, setIsCreating] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [newFieldEngineer] = useState(new SmartTeamsFieldEngineer({ defaultLocation: new SmartTeamsLocation({}) }));

    const { document, field, validate } = useForm<SmartTeamsFieldEngineer>(newFieldEngineer, {});

    return html`
      <div class="flex flex-col gap-4">
        <h3 class="text-lg font-bold">Create new Field Engineer</h3>

        <label class="form-control w-full">
          <div class="label">
            <span class="label-text">Partner Company *</span>
          </div>
          ${EntityPickerInput({
            ...field('partnerCompanyIds'),
            class: SmartTeamsCompany,
            filter: new AdornisFilter({}),
            selectionSet: { name: 1 },
            renderString: (c: SmartTeamsCompany) => c.name ?? c._id,
            multi: true,
          })}
        </label>

        <label class="form-control w-full">
          <div class="label"><span class="label-text">Name *</span></div>
          ${TextInput({ ...field('name'), placeholder: 'e.g. Jane Doe' })}
        </label>

        <label class="form-control w-full">
          <div class="label"><span class="label-text">Email *</span></div>
          ${TextInput({ ...field('email'), placeholder: 'e.g. jane@example.com', type: 'email' })}
        </label>

        <label class="form-control w-full">
          <div class="label"><span class="label-text">Password</span></div>
          ${TextInput({
            value: password,
            onChanged: value => setPassword(value ?? ''),
            placeholder: 'Leave empty if the field engineer should not log in',
            type: 'password',
          })}
          <div class="label">
            <span class="label-text-alt text-base-content/50">Leave empty if the field engineer should not log in</span>
          </div>
        </label>

        <label class="form-control w-full">
          <div class="label">
            <span class="label-text">First Language</span>
          </div>
          ${LanguagePicker({ ...field('firstLanguage') })}
        </label>

        <label class="form-control w-full">
          <div class="label">
            <span class="label-text">Second Language</span>
          </div>
          ${LanguagePicker({ ...field('secondLanguage'), placeholder: 'Optional' })}
        </label>

        <label class="form-control w-full">
          <div class="label"><span class="label-text">External ID</span></div>
          ${TextInput({ ...field('externalId'), placeholder: 'e.g. FR::Bordeaux::NSCFRAB099' })}
        </label>

        <label class="form-control w-full">
          <div class="label"><span class="label-text">ServiceNow Engineer</span></div>
          ${ServiceNowEngineerPicker({ ...field('serviceNowSysId'), placeholder: 'Search by name or email…' })}
        </label>

        <div class="divider">Default Location</div>

        <div class="grid grid-cols-2 gap-4">
          <label class="form-control w-full">
            <div class="label"><span class="label-text">City *</span></div>
            ${TextInput({ ...field('defaultLocation', 'city'), placeholder: 'e.g. Berlin' })}
          </label>

          <label class="form-control w-full">
            <div class="label">
              <span class="label-text">Postal Code *</span>
            </div>
            ${TextInput({ ...field('defaultLocation', 'zipCode'), placeholder: 'e.g. 10115' })}
          </label>
        </div>

        <div class="grid grid-cols-3 gap-4">
          <label class="form-control w-full col-span-2">
            <div class="label"><span class="label-text">Street</span></div>
            ${TextInput({ ...field('defaultLocation', 'street'), placeholder: 'e.g. Example Street' })}
          </label>

          <label class="form-control w-full">
            <div class="label"><span class="label-text">Number</span></div>
            ${TextInput({ ...field('defaultLocation', 'number'), placeholder: 'e.g. 42' })}
          </label>
        </div>

        <label class="form-control w-full">
          <div class="label"><span class="label-text">Country</span></div>
          ${DropdownSelectionInput({
            ...field('defaultLocation', 'country'),
            selectables: Object.values(Country) as string[],
            renderString: (country: string) => country.replace(/_/g, ' '),
            resolveID: (country: string) => country,
            placeholder: 'Select country...',
          })}
        </label>

        ${errorMessage ? html`<div class="alert alert-error text-sm">${errorMessage}</div>` : nothing}

        <div class="flex justify-end gap-2 mt-2">
          <button class="btn btn-ghost" ?disabled=${isCreating} @click=${props.onCancel}>Cancel</button>
          <button
            class="btn btn-primary"
            ?disabled=${isCreating}
            @click=${async () => {
              const error = await validate();
              if (error) return;

              // Create inside the dialog so server-side validation (e.g. a duplicate email) can be
              // shown inline without closing the dialog.
              setErrorMessage(null);
              setIsCreating(true);
              try {
                const newId = await createFieldEngineerWithUser(document, password)();
                if (newId) props.onCreated(newId);
              } catch (error: unknown) {
                setErrorMessage(error instanceof Error ? error.message : 'Failed to create field engineer.');
              } finally {
                setIsCreating(false);
              }
            }}
          >
            ${isCreating ? html`<span class="loading loading-spinner loading-sm"></span>` : nothing} Create
          </button>
        </div>
      </div>
    `;
  },
);

/**
 * Shows a dialog to create a new field engineer.
 * Returns the new field engineer's ID if created, or null if cancelled.
 */
export async function showCreateFieldEngineerDialog(): Promise<string | null> {
  const companies = (await getAllowedPartnerCompanies()({ _id: 1, name: 1 })) as SmartTeamsCompany[];

  if (!companies || companies.length === 0) {
    await showDialog<void>({
      content: ok => html`
        <div class="flex flex-col gap-4">
          <h3 class="text-lg font-bold text-error">No partner companies available</h3>
          <p>Please create a partner company first before creating a field engineer.</p>
          <div class="flex justify-end">
            <button class="btn btn-primary" @click=${() => ok()}>OK</button>
          </div>
        </div>
      `,
    }).catch(() => {});
    return null;
  }

  const newId = await showDialog<string | null>({
    modalBoxClasses: 'max-w-lg',
    content: (ok, cancel) =>
      CreateFieldEngineerForm({
        companies,
        onCreated: ok,
        onCancel: () => cancel(),
      }),
  }).catch(() => null);

  return newId ?? null;
}
